// Reusable CSV import flow: file picker → preview table → POST → result.
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { parseCSV, mapCSV } from "@/lib/csv";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

export interface CsvImporterProps<T extends Record<string, string | undefined>> {
  /** Friendly module title — "Contacts", "Leads", "Candidates" */
  module: string;
  /** Endpoint path, e.g. "/contacts/bulk-import" */
  endpoint: string;
  /** Route to navigate back to on done, e.g. "/contacts" */
  backTo: string;
  /** CRUD list query key to invalidate, e.g. ["/contacts"] */
  listQueryKey: unknown[];
  /** Header-name → field lookup (lowercased). */
  fieldMap: Record<string, keyof T>;
  /** Field that must be present on every row (dropped otherwise), OR a predicate function. */
  requiredField: keyof T | ((row: T) => boolean);
  /** Column names to show in preview. */
  previewColumns: Array<{ field: keyof T; label: string }>;
  /** Description shown under the header. */
  description: string;
}

export function CsvImporter<T extends Record<string, string | undefined>>(
  props: CsvImporterProps<T>,
) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [preview, setPreview] = useState<T[] | null>(null);
  const [unmapped, setUnmapped] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ created: number; skipped: { row: number; reason: string }[] } | null>(null);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const grid = parseCSV(text);
    if (grid.length < 2) { setError("CSV needs a header row + at least one data row."); return; }
    const { rows, unmapped } = mapCSV<T>(grid, props.fieldMap, props.requiredField);
    if (rows.length === 0) { setError(`No rows passed the required-field check.`); return; }
    setPreview(rows);
    setUnmapped(unmapped);
    setError(null);
    setResult(null);
  }

  async function doImport() {
    if (!preview) return;
    setBusy(true); setError(null);
    try {
      const { data } = await api.post(props.endpoint, { rows: preview });
      setResult(data);
      qc.invalidateQueries({ queryKey: props.listQueryKey });
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Import failed.");
    } finally { setBusy(false); }
  }

  return (
    <div className="p-6 space-y-4">
      <Card>
        <CardContent className="p-6 space-y-3">
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={onFile}
            className="block text-sm file:mr-3 file:rounded-md file:border file:bg-secondary file:px-3 file:py-1.5 file:text-sm"
          />
          <p className="text-xs text-muted-foreground">{props.description}</p>
          {unmapped.length > 0 && (
            <p className="text-xs text-amber-600">Ignored columns: {unmapped.join(", ")}</p>
          )}
        </CardContent>
      </Card>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {preview && !result && (
        <Card>
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">{preview.length} rows ready to import</div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setPreview(null)}>Clear</Button>
                <Button onClick={doImport} disabled={busy}>{busy ? "Importing…" : "Import"}</Button>
              </div>
            </div>
            <div className="max-h-80 overflow-auto rounded border text-xs">
              <table className="w-full">
                <thead className="sticky top-0 bg-card text-left">
                  <tr>
                    {props.previewColumns.map((c) => (
                      <th key={String(c.field)} className="p-2">{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.slice(0, 25).map((r, i) => (
                    <tr key={i} className="border-t">
                      {props.previewColumns.map((c) => (
                        <td key={String(c.field)} className="p-2 text-muted-foreground">{r[c.field] ?? ""}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.length > 25 && <div className="p-2 text-muted-foreground">+{preview.length - 25} more…</div>}
            </div>
          </CardContent>
        </Card>
      )}

      {result && (
        <Card>
          <CardContent className="p-6 space-y-2">
            <p className="text-sm">Imported <span className="font-semibold">{result.created}</span> {props.module.toLowerCase()}.</p>
            {result.skipped.length > 0 && (
              <details className="text-xs text-muted-foreground">
                <summary>{result.skipped.length} rows skipped</summary>
                <ul className="mt-2 space-y-1">
                  {result.skipped.slice(0, 50).map((s, i) => (
                    <li key={i}>Row {s.row}: {s.reason}</li>
                  ))}
                </ul>
              </details>
            )}
            <div className="pt-2">
              <Button onClick={() => navigate(props.backTo)}>Back to {props.module}</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
